import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const CONSENT_KEY = "vw_cookie_consent";

export const getConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!getConsent()) setVisible(true);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const persist = (payload) => {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...payload, ts: Date.now() }));
    } catch {
      /* storage blocked: keep going without persisting */
    }
    setVisible(false);
    window.dispatchEvent(new CustomEvent("vw:consent-updated", { detail: payload }));
  };

  const acceptAll = () => persist({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => persist({ necessary: true, analytics: false, marketing: false });
  const saveChoice = () => persist({ necessary: true, analytics, marketing });

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          data-testid="cookie-banner"
          className="fixed bottom-0 left-0 right-0 z-[9999] px-4 pb-4 md:px-6 md:pb-6 pointer-events-none"
        >
          <div className="max-w-[1500px] mx-auto pointer-events-auto">
            <div className="bg-black border border-[#D90000]/50 shadow-[0_20px_60px_rgba(217,0,0,0.35)] backdrop-blur-xl">
              {/* Top bar */}
              <div className="flex items-start gap-4 p-6 md:p-8">
                <div className="hidden md:flex w-12 h-12 border border-[#D90000] items-center justify-center shrink-0">
                  <Cookie size={22} className="text-[#D90000]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#D90000] mb-2">Vie privée</div>
                  <h3 className="font-impact text-2xl md:text-3xl leading-tight mb-3">
                    NOUS UTILISONS DES COOKIES.
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed max-w-3xl">
                    Nous utilisons des cookies techniques nécessaires au bon fonctionnement du site et, avec votre accord, des cookies de mesure d'audience et publicitaires pour améliorer votre expérience. Vous pouvez accepter, refuser ou personnaliser vos choix à tout moment.
                    {" "}
                    <Link to="/politique-confidentialite" className="text-[#D90000] hover:underline underline-offset-4">
                      En savoir plus
                    </Link>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={rejectAll}
                  aria-label="Refuser tous les cookies"
                  data-testid="cookie-close"
                  className="shrink-0 text-white/40 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Détails */}
              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35 }}
                    className="overflow-hidden border-t border-white/10"
                  >
                    <div className="px-6 md:px-8 py-6 space-y-4">
                      <ChoiceRow
                        label="Cookies nécessaires"
                        description="Indispensables au bon fonctionnement du site (session, formulaires)."
                        checked={true}
                        disabled
                        onChange={() => {}}
                      />
                      <ChoiceRow
                        label="Cookies analytiques"
                        description="Google Analytics — nous aident à comprendre l'utilisation du site."
                        checked={analytics}
                        onChange={() => setAnalytics((v) => !v)}
                        testid="cookie-toggle-analytics"
                      />
                      <ChoiceRow
                        label="Cookies marketing"
                        description="Meta Pixel — mesure l'efficacité de nos campagnes publicitaires."
                        checked={marketing}
                        onChange={() => setMarketing((v) => !v)}
                        testid="cookie-toggle-marketing"
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions */}
              <div className="border-t border-white/10 p-4 md:p-6 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                <button
                  type="button"
                  onClick={() => setShowDetails((v) => !v)}
                  data-testid="cookie-personalize"
                  className="text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-white transition-colors text-left"
                >
                  {showDetails ? "← Retour" : "Personnaliser →"}
                </button>
                <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
                  {showDetails ? (
                    <button
                      type="button"
                      onClick={saveChoice}
                      data-testid="cookie-save"
                      className="bg-[#D90000] hover:bg-white hover:text-black text-white text-[11px] uppercase tracking-[0.22em] font-bold px-6 py-3 transition-all border border-[#D90000] hover:border-white"
                    >
                      Enregistrer mes choix
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={rejectAll}
                        data-testid="cookie-reject"
                        className="border border-white/25 hover:border-white hover:bg-white hover:text-black text-white text-[11px] uppercase tracking-[0.22em] font-bold px-6 py-3 transition-all"
                      >
                        Refuser
                      </button>
                      <button
                        type="button"
                        onClick={acceptAll}
                        data-testid="cookie-accept"
                        className="bg-[#D90000] hover:bg-white hover:text-black text-white text-[11px] uppercase tracking-[0.22em] font-bold px-6 py-3 transition-all border border-[#D90000] hover:border-white"
                      >
                        Accepter tout
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ChoiceRow = ({ label, description, checked, onChange, disabled, testid }) => (
  <label className={`flex items-start gap-4 ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      data-testid={testid}
      className={`relative shrink-0 w-11 h-6 border transition-colors ${
        checked ? "bg-[#D90000] border-[#D90000]" : "bg-transparent border-white/30"
      } ${disabled ? "cursor-not-allowed" : ""}`}
    >
      <span
        className={`absolute top-0.5 h-4 w-4 bg-white transition-transform ${
          checked ? "translate-x-[22px]" : "translate-x-[2px]"
        }`}
      />
    </button>
    <div className="flex-1 min-w-0">
      <div className="text-white text-sm font-semibold">{label}</div>
      <div className="text-white/55 text-xs leading-relaxed mt-0.5">{description}</div>
    </div>
  </label>
);

export default CookieBanner;
